import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FaBan, FaSearch, FaSpinner, FaCheckCircle, FaTimes, FaArrowRight,
    FaExclamationTriangle, FaInfoCircle, FaUser, FaCalendarAlt, FaTag,
    FaHistory, FaGift, FaDollarSign,
} from 'react-icons/fa';

const CANCEL_ENDPOINT = 'https://api.peaknet.com.au/cancel';

const STATUS_OPTIONS = [
    { value: 'requested',         label: 'Requested',         tone: 'amber' },
    { value: 'retention_offered', label: 'Retention offered', tone: 'indigo' },
    { value: 'ceased',            label: 'Ceased',            tone: 'green' },
    { value: 'rejected',          label: 'Rejected',          tone: 'red' },
    { value: 'withdrawn',         label: 'Withdrawn',         tone: 'gray' },
];

const toneClass = (tone) => ({
    amber:  'bg-[#3c3836] text-[#fabd2f] border border-[#d79921]',
    indigo: 'bg-[#3c3836] text-[#83a598] border border-[#458588]',
    green:  'bg-[#3c3836] text-[#b8bb26] border border-[#98971a]',
    gray:   'bg-[#3c3836] text-[#bdae93] border border-[#504945]',
    red:    'bg-[#3c3836] text-[#fb4934] border border-[#cc241d]',
}[tone] || 'bg-[#3c3836] text-[#bdae93] border border-[#504945]');

const callApi = async (body) => {
    const r = await fetch(CANCEL_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
 'Authorization': `Bearer ${localStorage.getItem('adminToken') || ''}` },
        body: JSON.stringify(body),
    });
    const data = await r.json();
    if (!r.ok) throw new Error(data.error || `HTTP ${r.status}`);
    return data;
};

const formatDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-AU', {
            day: 'numeric', month: 'short', year: 'numeric',
            hour: '2-digit', minute: '2-digit',
            timeZone: 'Australia/Sydney',
        });
    } catch { return iso; }
};
const formatDateOnly = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleDateString('en-AU', {
            weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
        });
    } catch { return iso; }
};

const CancelRequestsManagement = () => {
    const [statusFilter, setStatusFilter] = useState('requested');
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [selected, setSelected] = useState(null);
    const [searchQ, setSearchQ] = useState('');

    const loadList = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const r = await callApi({ action: 'adminList', status: statusFilter, limit: 100 });
            setItems(r.items || []);
        } catch (e) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { loadList(); }, [loadList]);

    const filteredItems = useMemo(() => {
        if (!searchQ.trim()) return items;
        const q = searchQ.toLowerCase().trim();
        return items.filter((it) =>
            (it.customerEmail || '').toLowerCase().includes(q)
            || (it.customerName || '').toLowerCase().includes(q)
            || (it.cancelId || '').toLowerCase().includes(q)
            || (it.reason || '').toLowerCase().includes(q),
        );
    }, [items, searchQ]);

    const refreshSelected = useCallback(async () => {
        if (!selected) return;
        try {
            const r = await callApi({ action: 'adminGet', customerUID: selected.customerUID, cancelId: selected.cancelId });
            setSelected(r.item);
            await loadList();
        } catch (e) {
            setError(e.message);
        }
    }, [selected, loadList]);

    return (
        <div className="p-6 max-w-7xl mx-auto bg-[#1d2021] min-h-screen">
            <div className="flex items-center gap-3 mb-6">
                <FaBan className="text-3xl text-[#fb4934]" />
                <div>
                    <h1 className="text-2xl font-bold text-[#ebdbb2]">Cancel Requests</h1>
                    <p className="text-sm text-[#928374]">Customer-initiated cancellations. Each transition is manual — approve, offer retention, refund, or reject.</p>
                </div>
            </div>

            <div className="flex flex-wrap items-center gap-3 mb-5">
                <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-sm font-medium text-[#ebdbb2] focus:outline-none focus:border-[#fb4934]"
                >
                    {STATUS_OPTIONS.map((s) => (
                        <option key={s.value} value={s.value} className="bg-[#282828]">{s.label}</option>
                    ))}
                </select>
                <div className="relative flex-1 max-w-md">
                    <FaSearch className="absolute left-3 top-3 text-[#928374]" />
                    <input
                        type="text"
                        value={searchQ}
                        onChange={(e) => setSearchQ(e.target.value)}
                        placeholder="Filter by email, name, reason, or cancel ID…"
                        className="w-full pl-10 pr-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-sm text-[#ebdbb2] placeholder-[#928374] focus:outline-none focus:border-[#fb4934]"
                    />
                </div>
                <button
                    onClick={loadList}
                    disabled={loading}
                    className="px-4 py-2 bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-medium border border-[#3c3836] disabled:opacity-50"
                >
                    {loading ? <FaSpinner className="animate-spin" /> : 'Refresh'}
                </button>
                <span className="ml-auto text-sm text-[#928374]">
                    {filteredItems.length} request{filteredItems.length === 1 ? '' : 's'}
                </span>
            </div>

            {error && (
                <div className="mb-4 p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg text-sm text-[#fb4934]">
                    <FaExclamationTriangle className="inline mr-2" />{error}
                </div>
            )}

            {loading ? (
                <div className="flex items-center justify-center py-12 text-[#928374]"><FaSpinner className="animate-spin text-2xl" /></div>
            ) : filteredItems.length === 0 ? (
                <div className="text-center py-12 bg-[#282828] rounded-xl border border-[#3c3836]">
                    <FaInfoCircle className="text-3xl text-[#928374] mx-auto mb-2" />
                    <p className="text-[#928374]">No cancel requests with status "{statusFilter}".</p>
                </div>
            ) : (
                <div className="space-y-3">
                    {filteredItems.map((item) => (
                        <button
                            key={item.cancelId}
                            onClick={() => setSelected(item)}
                            className="w-full text-left bg-[#282828] border border-[#3c3836] hover:border-[#fb4934] hover:shadow-lg transition rounded-xl p-4 flex items-center gap-4"
                        >
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 mb-1">
                                    <span className="font-semibold text-[#ebdbb2]">{item.customerName || item.customerEmail}</span>
                                    <span className={`text-xs px-2 py-0.5 rounded-full ${toneClass(STATUS_OPTIONS.find((s) => s.value === item.status)?.tone)}`}>
                                        {item.status}
                                    </span>
                                </div>
                                <div className="text-xs text-[#bdae93] truncate">
                                    <strong className="text-[#ebdbb2]">Reason:</strong> {item.reason}
                                    {item.reasonDetail && <span className="text-[#928374]"> — {item.reasonDetail}</span>}
                                </div>
                                <div className="text-xs text-[#928374] mt-1">
                                    Cease: {formatDateOnly(item.ceaseDate)} · Plan: {item.currentPlan} (${item.currentMonthlyFee}/mo)
                                </div>
                            </div>
                            <FaArrowRight className="text-[#928374]" />
                        </button>
                    ))}
                </div>
            )}

            {selected && <CancelDetail item={selected} onClose={() => setSelected(null)} onRefresh={refreshSelected} />}
        </div>
    );
};

// ───────── detail drawer ─────────

const CancelDetail = ({ item, onClose, onRefresh }) => {
    const [busy, setBusy] = useState(false);
    const [actionError, setActionError] = useState('');
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [showRetentionModal, setShowRetentionModal] = useState(false);

    const transition = useCallback(async (action, extra = {}) => {
        if (action === 'adminReject') {
            const reason = prompt('Reason for rejection?');
            if (reason === null) return;
            extra.reason = reason;
        } else if (action === 'adminApprove' && !confirm(`Approve cancellation? This will:\n\n• Cease the Vocus service on ${formatDateOnly(item.ceaseDate)}\n• Release the customer's email for re-signup\n• Delete the Cognito user\n• Cancel any open Move Home requests\n\nThis is FINAL.`)) {
            return;
        }
        setBusy(true);
        setActionError('');
        try {
            await callApi({
                action,
                customerUID: item.customerUID,
                cancelId: item.cancelId,
                actor: 'admin',
                ...extra,
            });
            await onRefresh();
        } catch (e) {
            setActionError(e.message);
        } finally {
            setBusy(false);
        }
    }, [item, onRefresh]);

    const status = item.status;
    const isOpen = status === 'requested' || status === 'retention_offered';

    return (
        <div className="fixed inset-0 z-50 flex justify-end">
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />
            <div className="relative w-full max-w-2xl bg-[#1d2021] h-full overflow-y-auto shadow-2xl border-l border-[#3c3836]">
                <div className="sticky top-0 bg-[#1d2021] z-10 border-b border-[#3c3836] p-4 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <span className={`text-xs font-semibold px-2 py-1 rounded-full ${toneClass(STATUS_OPTIONS.find((s) => s.value === status)?.tone)}`}>
                            {status}
                        </span>
                        <code className="text-sm text-[#bdae93]">{item.cancelId}</code>
                    </div>
                    <button onClick={onClose} className="text-[#928374] hover:text-[#ebdbb2]"><FaTimes /></button>
                </div>

                <div className="p-6 space-y-6">
                    {actionError && (
                        <div className="p-3 bg-[#3c3836] border border-[#fb4934] rounded-lg text-sm text-[#fb4934]">
                            <FaExclamationTriangle className="inline mr-2" />{actionError}
                        </div>
                    )}

                    <Section icon={<FaUser />} title="Customer">
                        <KV label="Name" value={item.customerName} />
                        <KV label="Email" value={item.customerEmail} />
                        <KV label="UID" value={<code className="text-xs text-[#bdae93]">{item.customerUID}</code>} />
                    </Section>

                    <Section icon={<FaCalendarAlt />} title="Request">
                        <KV label="Reason" value={item.reason} />
                        {item.reasonDetail && <KV label="Detail" value={item.reasonDetail} />}
                        <KV label="Cease date" value={formatDateOnly(item.ceaseDate)} />
                        <KV label="Submitted" value={formatDate(item.createdAt)} />
                        <KV label="T&Cs accepted" value={item.tcsAccepted ? `Yes (${formatDate(item.tcsAcceptedAt)})` : 'No'} />
                    </Section>

                    <Section icon={<FaTag />} title="Service">
                        <KV label="Current plan" value={`${item.currentPlan || '—'} ($${item.currentMonthlyFee || 0}/mo)`} />
                        <KV label="AVCID" value={item.avcId || '—'} />
                        <KV label="ServiceID" value={item.serviceId || '—'} />
                        <KV label="Final invoice" value={
                            item.finalInvoiceId
                                ? `${item.finalInvoiceId} — $${Number(item.finalInvoiceAmount || 0).toFixed(2)} (paid ${formatDateOnly(item.finalInvoicePaidAt)})`
                                : '—'
                        } />
                    </Section>

                    {item.retentionOffer && (
                        <Section icon={<FaGift />} title="Retention offer made">
                            <KV label="Type" value={item.retentionOffer.offerType} />
                            <KV label="Details" value={
                                <pre className="text-xs bg-[#282828] border border-[#3c3836] p-2 rounded text-[#ebdbb2]">{JSON.stringify(item.retentionOffer.offerDetails || {}, null, 2)}</pre>
                            } />
                            <KV label="Offered at" value={formatDate(item.retentionOfferedAt)} />
                            <KV label="Offered by" value={item.retentionOfferedBy} />
                        </Section>
                    )}

                    {item.vocusCeaseResult && (
                        <Section icon={<FaCheckCircle />} title="Vocus cease result">
                            <pre className="text-xs bg-[#282828] border border-[#3c3836] p-2 rounded overflow-x-auto text-[#ebdbb2]">{JSON.stringify(item.vocusCeaseResult, null, 2)}</pre>
                        </Section>
                    )}

                    {item.refundResult && (
                        <Section icon={<FaDollarSign />} title="Refund issued">
                            <pre className="text-xs bg-[#282828] border border-[#3c3836] p-2 rounded text-[#ebdbb2]">{JSON.stringify(item.refundResult, null, 2)}</pre>
                        </Section>
                    )}

                    {item.lockedEmail && (
                        <Section icon={<FaInfoCircle />} title="Email released">
                            <KV label="Original email" value={item.customerEmail} />
                            <KV label="Locked as" value={<code className="text-xs text-[#bdae93]">{item.lockedEmail}</code>} />
                            <KV label="Available for new signup" value={<span className="text-[#b8bb26]">✓ Yes</span>} />
                        </Section>
                    )}

                    {Array.isArray(item.auditLog) && item.auditLog.length > 0 && (
                        <Section icon={<FaHistory />} title="Audit log">
                            <ul className="space-y-2 text-xs text-[#bdae93]">
                                {[...item.auditLog].reverse().map((entry, idx) => (
                                    <li key={idx} className="flex gap-3 border-l-2 border-[#cc241d] pl-3">
                                        <span className="text-[#928374] shrink-0 w-32">{formatDate(entry.at)}</span>
                                        <span className="flex-1 text-[#ebdbb2]">
                                            <strong>{entry.action}</strong>
                                            {entry.actor && ` · ${entry.actor}`}
                                            {entry.reason && ` — ${entry.reason}`}
                                        </span>
                                    </li>
                                ))}
                            </ul>
                        </Section>
                    )}

                    {isOpen && (
                        <div className="border-t border-[#3c3836] pt-5 space-y-2">
                            <div className="text-sm font-bold text-[#fabd2f] mb-2">Admin actions</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                <button
                                    onClick={() => transition('adminApprove')}
                                    disabled={busy}
                                    className="px-4 py-3 bg-[#fb4934] hover:bg-[#cc241d] text-[#1d2021] rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    {busy ? <FaSpinner className="animate-spin" /> : <FaBan />} Approve & Cease
                                </button>
                                <button
                                    onClick={() => setShowRefundModal(true)}
                                    disabled={busy}
                                    className="px-4 py-3 bg-[#fabd2f] hover:bg-[#d79921] text-[#1d2021] rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <FaDollarSign /> Approve + Refund
                                </button>
                                <button
                                    onClick={() => setShowRetentionModal(true)}
                                    disabled={busy}
                                    className="px-4 py-3 bg-[#83a598] hover:bg-[#458588] text-[#1d2021] rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <FaGift /> Offer Retention
                                </button>
                                <button
                                    onClick={() => transition('adminReject')}
                                    disabled={busy}
                                    className="px-4 py-3 bg-[#3c3836] hover:bg-[#504945] text-[#ebdbb2] border border-[#504945] rounded-lg text-sm font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-50"
                                >
                                    <FaTimes /> Reject
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {showRefundModal && (
                <RefundModal
                    item={item}
                    onClose={() => setShowRefundModal(false)}
                    onConfirm={async (amount, reason) => {
                        setShowRefundModal(false);
                        await transition('adminApproveWithRefund', { refundAmount: amount, refundReason: reason });
                    }}
                />
            )}
            {showRetentionModal && (
                <RetentionModal
                    item={item}
                    onClose={() => setShowRetentionModal(false)}
                    onConfirm={async (offerType, offerDetails) => {
                        setShowRetentionModal(false);
                        await transition('adminOfferRetention', { offerType, offerDetails });
                    }}
                />
            )}
        </div>
    );
};

// ───────── refund modal ─────────

const RefundModal = ({ item, onClose, onConfirm }) => {
    const [amount, setAmount] = useState(item.currentMonthlyFee || 0);
    const [reason, setReason] = useState('goodwill_cancellation');
    return (
        <ModalWrapper title="Approve + Issue Refund" onClose={onClose}>
            <p className="text-sm text-[#bdae93] mb-4">
                This will <strong className="text-[#ebdbb2]">cease the service</strong> AND issue a refund to the customer's last-paid invoice via Stripe.
            </p>
            <Field label="Refund amount (AUD)">
                <input
                    type="number" step="0.01" min="0"
                    value={amount}
                    onChange={(e) => setAmount(Number(e.target.value))}
                    className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2] focus:outline-none focus:border-[#fabd2f]"
                />
            </Field>
            <Field label="Reason">
                <select
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]"
                >
                    <option value="goodwill_cancellation">Goodwill (cancellation)</option>
                    <option value="service_fault">Service fault</option>
                    <option value="billing_error">Billing error</option>
                    <option value="other">Other</option>
                </select>
            </Field>
            <div className="flex justify-end gap-2 mt-5">
                <button onClick={onClose} className="px-4 py-2 border border-[#3c3836] bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-semibold">Cancel</button>
                <button
                    onClick={() => onConfirm(amount, reason)}
                    disabled={amount <= 0}
                    className="px-4 py-2 bg-[#fabd2f] hover:bg-[#d79921] text-[#1d2021] rounded-lg text-sm font-semibold disabled:opacity-50"
                >
                    Cease + refund ${amount.toFixed(2)}
                </button>
            </div>
        </ModalWrapper>
    );
};

// ───────── retention modal ─────────

const RetentionModal = ({ item, onClose, onConfirm }) => {
    const [offerType, setOfferType] = useState('extend_promo');
    const [durationMonths, setDurationMonths] = useState(3);
    const [amount, setAmount] = useState(item.currentMonthlyFee - 10);
    const [newPlan, setNewPlan] = useState('Home Standard');
    const [creditAmount, setCreditAmount] = useState(50);
    const [note, setNote] = useState('');

    const submit = () => {
        onConfirm(offerType, { durationMonths, amount, newPlan, creditAmount, note });
    };

    return (
        <ModalWrapper title="Offer Retention" onClose={onClose}>
            <p className="text-sm text-[#bdae93] mb-4">
                Make a retention offer instead of cancelling. The customer's pricing override updates immediately.
            </p>
            <Field label="Offer type">
                <select
                    value={offerType}
                    onChange={(e) => setOfferType(e.target.value)}
                    className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]"
                >
                    <option value="extend_promo">Extend current promo by N months</option>
                    <option value="one_free_month">Give one free month</option>
                    <option value="discount">Apply discount for N months</option>
                    <option value="plan_downgrade">Downgrade to a cheaper plan</option>
                    <option value="credit">Apply credit to next invoice</option>
                </select>
            </Field>

            {offerType === 'extend_promo' && (
                <Field label="Extend by (months)">
                    <input type="number" min="1" max="24" value={durationMonths}
                        onChange={(e) => setDurationMonths(Number(e.target.value))}
                        className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]" />
                </Field>
            )}

            {offerType === 'discount' && (
                <>
                    <Field label="New monthly fee (AUD)">
                        <input type="number" step="0.01" min="0" value={amount}
                            onChange={(e) => setAmount(Number(e.target.value))}
                            className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]" />
                    </Field>
                    <Field label="Duration (months)">
                        <input type="number" min="1" max="24" value={durationMonths}
                            onChange={(e) => setDurationMonths(Number(e.target.value))}
                            className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]" />
                    </Field>
                </>
            )}

            {offerType === 'plan_downgrade' && (
                <Field label="Downgrade to plan">
                    <select value={newPlan} onChange={(e) => setNewPlan(e.target.value)}
                        className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]">
                        <option value="Home Basic">Home Basic — $59.99</option>
                        <option value="Home Standard">Home Standard — $79.99</option>
                        <option value="Home Fast">Home Fast — $89.99</option>
                    </select>
                </Field>
            )}

            {offerType === 'credit' && (
                <Field label="Credit amount (AUD)">
                    <input type="number" step="0.01" min="0" value={creditAmount}
                        onChange={(e) => setCreditAmount(Number(e.target.value))}
                        className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]" />
                </Field>
            )}

            <Field label="Internal note (optional)">
                <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2}
                    className="w-full px-3 py-2 bg-[#282828] border border-[#3c3836] rounded-lg text-[#ebdbb2]" />
            </Field>

            <div className="flex justify-end gap-2 mt-5">
                <button onClick={onClose} className="px-4 py-2 border border-[#3c3836] bg-[#282828] hover:bg-[#3c3836] text-[#ebdbb2] rounded-lg text-sm font-semibold">Cancel</button>
                <button onClick={submit} className="px-4 py-2 bg-[#83a598] hover:bg-[#458588] text-[#1d2021] rounded-lg text-sm font-semibold">Offer</button>
            </div>
        </ModalWrapper>
    );
};

// ───────── small helpers ─────────

const ModalWrapper = ({ title, children, onClose }) => (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4">
        <div className="bg-[#282828] border border-[#3c3836] rounded-2xl max-w-md w-full shadow-2xl">
            <div className="px-5 py-3 border-b border-[#3c3836] flex items-center justify-between">
                <h3 className="font-bold text-[#ebdbb2]">{title}</h3>
                <button onClick={onClose} className="text-[#928374] hover:text-[#ebdbb2]"><FaTimes /></button>
            </div>
            <div className="p-5">{children}</div>
        </div>
    </div>
);

const Section = ({ icon, title, children }) => (
    <div>
        <div className="flex items-center gap-2 mb-2 text-sm font-semibold text-[#fb4934]">
            <span className="text-[#928374]">{icon}</span> {title}
        </div>
        <div className="space-y-1 text-sm text-[#ebdbb2] pl-6">{children}</div>
    </div>
);

const KV = ({ label, value }) => (
    <div className="flex gap-3">
        <span className="text-[#928374] w-36 shrink-0">{label}</span>
        <span className="flex-1 text-[#ebdbb2]">{value}</span>
    </div>
);

const Field = ({ label, children }) => (
    <label className="block mb-3">
        <span className="text-xs font-semibold text-[#bdae93] uppercase tracking-wider">{label}</span>
        <div className="mt-1">{children}</div>
    </label>
);

export default CancelRequestsManagement;
